/**
 * The chat rail beside a single surface — the layout every product that wants
 * "keep talking about the thing you're looking at" shares.
 *
 * The workspace owns the split and the rail's visibility; it does not own what
 * the surface is or where it came from. Hosts pass a descriptor and keep their
 * own URL in step with it, so the artifact route can stay the artifact's
 * permalink while chat-home encodes the same surface in a query param.
 *
 * collapsed/onCollapsedChange are fully controlled by the host so the
 * open/closed state can live in the URL rather than in local component state.
 */

import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import {
  AgentChatContext,
  AgentChatPanel,
  ChatRailShortcut,
} from '@/components/agent-chat';
import {
  SurfaceHost,
  type ArtifactGallerySurfaceDescriptor,
  type SurfaceDescriptor,
} from '@/components/surface-host/surface-host';
import { cn } from '@/lib/utils';
import { useOperatorConversationStore } from '@/stores/operator-conversation-store';
import type { ArtifactListItem } from '@/types/artifacts';

export interface ChatWorkspaceRailContext {
  /** A surface sits beside the rail, so collapsing it leaves something behind. */
  surfaceOpen: boolean;
  /** Collapses the rail to the shortcut bar. */
  onCollapse: () => void;
}

export interface ChatWorkspaceProps {
  agentId: string;
  /** The surface beside the rail, or null for a full-width chat. */
  surface: SurfaceDescriptor | null;
  /**
   * Called when the surface's close affordance is activated. Omit to hide it:
   * what closing means depends on what is behind the surface, so only the host
   * knows whether there is anywhere to go.
   */
  onCloseSurface?: () => void;
  /**
   * Called after a workflow surface's workflow is deleted. Omit to fall back
   * to `onCloseSurface`, which the host may itself withhold.
   */
  onWorkflowDeleted?: () => void;
  /**
   * The rail's header. Takes the workspace's state rather than owning it, so
   * a host can render the collapse control among its own chrome.
   */
  renderRailHeader?: (ctx: ChatWorkspaceRailContext) => ReactNode;
  /** Shown only with no surface — beside one, the rail is too narrow to greet. */
  emptyState?: ReactNode;
  emptyStateActions?: ReactNode;
  composerPlaceholder?: string;
  pendingFirstTurn?: boolean;
  /**
   * Composer "+" menu items that only some hosts want. Chat home offers the
   * full menu; artifact chat is a rail beside one artifact, where running or
   * authoring a workflow is off-topic.
   */
  skillsEnabled?: boolean;
  workflowCommands?: boolean;
  /**
   * Whether the rail is collapsed to its shortcut bar. Fully controlled by
   * the host so the state can live in the URL.
   */
  collapsed: boolean;
  /**
   * Called when the workspace wants to change the collapsed state (user
   * pressed Hide/Show, or a closing surface forces the rail open).
   */
  onCollapsedChange: (collapsed: boolean) => void;
  /**
   * Called when the workflow surface's "View Artifacts" button is activated.
   * Opens the gallery in the surface slot. When omitted, the editor falls
   * back to navigating to the standalone /artifacts page.
   */
  onViewWorkflowArtifacts?: (workflowId: string) => void;
  /**
   * Called when a card inside the artifact gallery pane is clicked. When
   * supplied the artifact opens as a layered surface instead of navigating
   * to the standalone detail route.
   */
  onOpenGalleryArtifact?: (artifact: ArtifactListItem) => void;
  /** Called when the gallery pane changes one of its persisted filters. */
  onGalleryChange?: (surface: ArtifactGallerySurfaceDescriptor) => void;
  /**
   * Called when the workflow surface moves to another run (or clears one) so
   * the host can keep ?runId= naming what the pane shows.
   */
  onRunChange?: (workflowId: string, runId: string | null) => void;
  /**
   * Re-enables the workflow surface's unsaved-changes navigation blocker.
   * Only meaningful when the surface being shown is the host route's own
   * anchor workflow rather than a layered peek at another one.
   */
  warnOnUnsavedChanges?: boolean;
}

export function ChatWorkspace({
  agentId,
  surface,
  onCloseSurface,
  onWorkflowDeleted,
  renderRailHeader,
  emptyState,
  emptyStateActions,
  composerPlaceholder,
  pendingFirstTurn,
  skillsEnabled = false,
  workflowCommands = false,
  collapsed,
  onCollapsedChange,
  onViewWorkflowArtifacts,
  onOpenGalleryArtifact,
  onGalleryChange,
  onRunChange,
  warnOnUnsavedChanges,
}: ChatWorkspaceProps) {
  const surfaceOpen = surface !== null;

  // A surface offering a workflow should land in this workspace rather than
  // navigate out of it, and the host already says where workflows go when it
  // wires up the chat — the same route the chat's own workflow cards take.
  // A run id rides along, as it does from those cards: the pane opens on
  // that run rather than on the bare workflow. The pane reports the run it
  // later moves to through the same door, so the host rewrites the surface
  // -- and with it the URL -- and a reload lands on what was on screen.
  const onOpenWorkflow = useContext(AgentChatContext)?.onOpenWorkflow;

  // With no surface to fall back on, a collapsed rail would leave a blank
  // page — so closing the surface always brings the rail back. Only fire when
  // chat is actually hidden; otherwise a normal surface-close would write
  // ?chat=1 unnecessarily.
  useEffect(() => {
    if (!surfaceOpen && collapsed) onCollapsedChange(false);
  }, [surfaceOpen, collapsed, onCollapsedChange]);

  // A run asking the user something brings a collapsed rail back: the
  // question is only answerable in the chat.
  const attentionSeq = useOperatorConversationStore(
    (state) => state.attentionSeq,
  );
  const seenAttentionRef = useRef(attentionSeq);
  useEffect(() => {
    if (attentionSeq === seenAttentionRef.current) return;
    seenAttentionRef.current = attentionSeq;
    if (collapsed) onCollapsedChange(false);
  }, [attentionSeq, collapsed, onCollapsedChange]);

  const showChat = !surfaceOpen || !collapsed;

  // Paint the surface at width 0 first, then expand on the next frame so the
  // chat column can interpolate from 100% to the rail instead of snapping.
  const [splitExpanded, setSplitExpanded] = useState(surfaceOpen);
  useEffect(() => {
    if (!surfaceOpen) {
      setSplitExpanded(false);
      return;
    }
    const frame = requestAnimationFrame(() => setSplitExpanded(true));
    return () => cancelAnimationFrame(frame);
  }, [surfaceOpen]);

  return (
    <div className='relative flex h-full min-h-0 w-full overflow-hidden'>
      {showChat && (
        <main
          className={cn(
            // The chat is its own surface, a shade off the canvas beside it.
            'flex min-h-0 min-w-0 flex-col bg-sidebar',
            'transition-[flex-basis,flex-grow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
            splitExpanded && 'border-r border-border/60',
          )}
          style={{
            flexGrow: splitExpanded ? 0 : 1,
            flexShrink: 0,
            flexBasis: splitExpanded ? '24rem' : '100%',
          }}
        >
          {renderRailHeader?.({
            surfaceOpen,
            onCollapse: () => onCollapsedChange(true),
          })}

          <AgentChatPanel
            agentId={agentId}
            layout={surfaceOpen ? 'rail' : 'full'}
            composerPlaceholder={composerPlaceholder}
            customInstructionsEnabled
            clarificationSettingsEnabled
            skillsEnabled={skillsEnabled}
            workflowCommands={workflowCommands}
            emptyState={surfaceOpen ? null : emptyState}
            emptyStateActions={surfaceOpen ? null : emptyStateActions}
            pendingFirstTurn={pendingFirstTurn}
          />
        </main>
      )}

      {surface && (
        <div
          className={cn(
            'flex min-h-0 min-w-0 flex-col overflow-hidden',
            'transition-[flex-grow,opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
            splitExpanded
              ? 'opacity-100 translate-x-0'
              : 'pointer-events-none opacity-0 translate-x-6',
          )}
          style={{
            flexGrow: splitExpanded ? 1 : 0,
            flexShrink: 0,
            flexBasis: '0%',
          }}
        >
          <SurfaceHost
            surface={surface}
            onViewWorkflow={onOpenWorkflow}
            onRunChange={
              // Prefer the host's own onRunChange (so a route whose workflow
              // is the path anchor can write ?runId= without a surface param);
              // fall back to re-opening via onOpenWorkflow so chat-home keeps
              // its existing behaviour.
              onRunChange ??
              (onOpenWorkflow &&
                ((workflowId, runId) =>
                  onOpenWorkflow(workflowId, runId ?? undefined)))
            }
            onViewArtifacts={onViewWorkflowArtifacts}
            onOpenGalleryArtifact={onOpenGalleryArtifact}
            onGalleryChange={onGalleryChange}
            onClose={onCloseSurface}
            onWorkflowDeleted={onWorkflowDeleted}
            warnOnUnsavedChanges={warnOnUnsavedChanges}
          />
        </div>
      )}

      {!showChat && (
        <ChatRailShortcut onClick={() => onCollapsedChange(false)} />
      )}
    </div>
  );
}
